import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";
import {matchPath} from "react-router";

import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {enums, withConversionContext} from "@khan/analytics-learning-time";
import {emitCustomHomepageGreeting} from "@khan/events-homepage";
import Log, {Errors} from "@khan/logging";
import {a11yStyles, mediaQueries} from "@khan/shared-styles";

import PageRoot from "#app-shell-package/page-root.tsx";
import DefaultPageTemplate from "#app-shell-package/page-templates/default-page-template.tsx";
import DefaultWithKhanmigoPageTemplate from "#app-shell-package/page-templates/with-khanmigo-templates/default-with-khanmigo-page-template.tsx";
import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import MaybeReturnLink from "#components/maybe-return-link-package/maybe-return-link.tsx";
import ApolloQuery from "#data-access/gql/apollo-query.tsx";
import {
    withFeatureIsOn,
    type WithFeatureIsOn,
    type WithoutFeatureIsOn,
} from "#data-access/with-feature-is-on.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import {SkillsToProficientOverallBanner} from "#skills-to-proficient/index.tsx";

import ContentNav from "./content-nav.tsx";
import {
    getKaidOrUsernameFromPath,
    initializeDefaultProfile,
    isKaidValid,
    kaidIsDefault,
} from "./content-pages/profile-util.ts";
import Heading from "./heading.tsx";
import {
    getClassDescriptor,
    HOMEPAGE_QUERY,
    isActivityAccessible,
    userNotFound,
    type HomepageQuery,
} from "./homepage-data.ts";
import {withStrings} from "./locales/logged-in-homepage/load-strings.tsx";
import SideNav from "./side-nav/side-nav.tsx";

type Props = WithLocationProps & WithUserProfileProps & WithFeatureIsOn;

type State = {
    showError: boolean;
};

class LoggedInHomepage extends React.Component<Props, State> {
    state: State = {
        showError: false,
    };

    // on every page load, send an emitGuideGreeting event
    componentDidMount() {
        if (this.props.featureIsOn) {
            // need to delay the greeting to ensure the guide is ready
            // eslint-disable-next-line no-restricted-syntax
            setTimeout(() => {
                emitCustomHomepageGreeting({greetingType: "summary"});
            }, 2000);
        }
    }

    handleGraphQLError: (error: Error) => void = (error) => {
        Log.error("Failed to query LIHP sidenav", Errors.Internal, {
            cause: error,
        });
        this.setState({showError: true});
    };

    getQueryVariables: (kaid: string) => {
        kaid?: string | null | undefined;
        username?: string | null | undefined;
    } = (kaid) => {
        if (kaidIsDefault(kaid)) {
            return {} as any;
        }

        return isKaidValid(kaid) ? {kaid} : {username: kaid};
    };

    renderGrid: (
        kaidOrUsername: string | null | undefined,
        query: HomepageQuery,
    ) => React.ReactNode = (kaidOrUsername, query) => {
        const {showError} = this.state;

        const refetch = () => {
            query.fetchMore({
                variables: this.getQueryVariables(kaidOrUsername ?? ""),
                updateQuery: (prevResult, {fetchMoreResult}) => {
                    if (!fetchMoreResult) {
                        return prevResult;
                    }

                    return fetchMoreResult;
                },
            });
        };
        const content = <ContentNav query={query} refetch={refetch} />;

        return (
            <View style={styles.grid}>
                <View style={styles.sideNav}>
                    <SideNav query={query} showError={showError} />
                </View>
                <View style={styles.content}>{content}</View>
            </View>
        );
    };

    renderSkillsToProficientBanner = (
        query: HomepageQuery,
    ): React.ReactNode => {
        const {location} = this.props;
        // Provide the banner with the class descriptor whenever we're on a
        // classroom LIHP.
        const match = matchPath<{coachKaid: string; classroomId: string}>(
            location.pathname,
            {
                path: "/profile/me/teacher/:coachKaid/class/:classroomId",
                exact: true,
            },
        );
        const classDescriptor = getClassDescriptor(
            match?.params?.classroomId,
            match?.params?.coachKaid,
            query,
        );

        return (
            <SkillsToProficientOverallBanner
                classDescriptor={classDescriptor}
            />
        );
    };

    renderPage: (query: HomepageQuery) => React.ReactNode = (query) => {
        const {location, profileLoading} = this.props;

        const kaidOrUsername = getKaidOrUsernameFromPath(location.pathname);
        const userIsNotFound = userNotFound(query);

        // Don't show the heading until we have a profile, because doing so
        // will cause us to load profile data before we've properly cached
        // This means if you are looking at someone else's profile, the
        // heading will wait for your profile, which isn't necessary, but
        // the UX isn't comprimized in practice
        return (
            <React.Fragment>
                <h1
                    data-testid="dashboard-a11y-heading"
                    className={css(a11yStyles.srOnly)}
                >
                    {t`Learner home`}
                </h1>
                {!profileLoading && (
                    <Heading
                        profileKaid={kaidOrUsername}
                        isActivityAccessible={isActivityAccessible(query)}
                        userNotFound={userIsNotFound}
                    />
                )}
                {!userIsNotFound && this.renderGrid(kaidOrUsername, query)}
            </React.Fragment>
        );
    };

    render(): React.ReactNode {
        const {location, profile} = this.props;

        const pageRootTemplate = this.props.featureIsOn
            ? DefaultWithKhanmigoPageTemplate
            : DefaultPageTemplate;

        if (profile) {
            initializeDefaultProfile(profile);
        }

        let kaidOrUsername = getKaidOrUsernameFromPath(location.pathname) || "";

        if (kaidIsDefault(kaidOrUsername)) {
            // For the default route, there is no real point in loading the
            // query until we know more about the user.  Wait for the profile
            // and use the kaid or username from the profileRoot as the query
            // variable
            if (!profile) {
                return (
                    <PageRoot settings={{title: t`Dashboard`, noIndex: true}}>
                        <RouteLoadSpinner />
                    </PageRoot>
                );
            }
            kaidOrUsername =
                getKaidOrUsernameFromPath(profile.get("profileRoot")) || "";
        }

        return (
            <PageRoot
                template={pageRootTemplate}
                settings={{title: t`Dashboard`, noIndex: true}}
            >
                <View style={styles.page}>
                    <ApolloQuery
                        query={HOMEPAGE_QUERY}
                        fetchPolicy="cache-first"
                        onError={this.handleGraphQLError}
                        variables={this.getQueryVariables(kaidOrUsername)}
                    >
                        {(queryResult) => (
                            <>
                                {this.renderSkillsToProficientBanner(
                                    queryResult,
                                )}
                                <MaybeReturnLink />
                                {this.renderPage(queryResult)}
                            </>
                        )}
                    </ApolloQuery>
                </View>
            </PageRoot>
        );
    }
}

const styles = StyleSheet.create({
    page: {
        background: color.white,

        [mediaQueries.smOrSmaller]: {
            background: color.offBlack8,
            paddingBottom: 16,
        },
    },

    grid: {
        [mediaQueries.mdOrLarger]: {
            display: "grid",
            gridTemplateColumns: "3fr 10fr",
            gap: spacing.xLarge_32,
            width: "100%",
            maxWidth: 1168,
            margin: "0 auto",
            paddingLeft: spacing.large_24,
            paddingRight: spacing.large_24,
        },
    },

    sideNav: {
        [mediaQueries.mdOrLarger]: {
            marginTop: spacing.xSmall_8,
            borderRight: `1px solid ${color.offBlack16}`,
        },
        [mediaQueries.smOrSmaller]: {
            background: color.white,
            border: `solid 1px ${color.offBlack16}`,
            marginTop: spacing.medium_16,
            paddingTop: spacing.xSmall_8,
            paddingLeft: spacing.medium_16,
            paddingRight: spacing.medium_16,
        },
        "@media print": {
            display: "none",
        },
    },

    content: {
        paddingTop: spacing.large_24,
        minHeight: 600,
        [mediaQueries.smOrSmaller]: {
            background: color.white,
            border: `solid 1px ${color.offBlack16}`,
            marginTop: spacing.medium_16,
            paddingLeft: spacing.medium_16,
            paddingRight: spacing.medium_16,
        },
    },
});

type ExportProps = WithoutLocation<
    WithoutFeatureIsOn<
        WithoutUserProfile<
            JSX.LibraryManagedAttributes<
                typeof LoggedInHomepage,
                React.ComponentProps<typeof LoggedInHomepage>
            >
        >
    >
>;

export default compose(
    withStrings,
    withConversionContext({
        activity: enums.Activity.NAVIGATING,
    }),
    withLocation,
    withUserProfile,
    // Feature flag to enable the AI guide memory feature
    withFeatureIsOn("lihp-welcome-message"),
)(LoggedInHomepage) as React.ComponentType<ExportProps>;
